class JklGooviDayEvents extends HTMLElement {
  constructor() {
    super();
    
    this.sectionId = this.dataset.sectionId;
    this.googleMapsApiKey = this.dataset.googleMapsApiKey;
    
    // Parse events data
    try {
      this.events = JSON.parse(this.dataset.events || '[]');
      this.eventDates = JSON.parse(this.dataset.eventDates || '[]');
    } catch (e) {
      console.error('[JKL Goovi Day Events] Error parsing events data:', e);
      this.events = [];
      this.eventDates = [];
    }
    
    // Parse translations
    try {
      this.t = JSON.parse(this.dataset.translations || '{}');
    } catch (e) {
      console.error('[JKL Goovi Day Events] Error parsing translations:', e);
      this.t = {};
    }
    
    // State
    this.currentYear = new Date().getFullYear();
    this.currentMonth = new Date().getMonth();
    this.selectedDate = null;
    this.selectedEvent = null;
    this.searchTerm = '';
    this.searchResultDates = [];
    
    this.locale = this.t.locale || 'it-IT';
    this.monthNames = this.t.months || [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    // DOM elements
    this.calendarEl = null;
    this.eventListEl = null;
    this.eventMapEl = null;
    this.searchInput = null;
    this.clearBtn = null;
    this.mapInstance = null;
    this.markerInstance = null;
    
    // Bind methods
    this.init = this.init.bind(this);
    this.renderCalendar = this.renderCalendar.bind(this);
    this.handleDateClick = this.handleDateClick.bind(this);
    this.handleEventClick = this.handleEventClick.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.handleClearSearch = this.handleClearSearch.bind(this);
    this.renderEventList = this.renderEventList.bind(this);
    this.showEventMap = this.showEventMap.bind(this);
  }

  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      requestAnimationFrame(() => this.init());
    }
  }

  init() {
    this.calendarEl = this.querySelector('.jkl-goovi-calendar');
    this.eventListEl = this.querySelector('.jkl-goovi-event-list');
    this.eventMapEl = this.querySelector('.jkl-goovi-event-map');
    this.searchInput = this.querySelector('.jkl-goovi-day-events__search-input');
    this.clearBtn = this.querySelector('.jkl-goovi-day-events__clear-btn');
    
    if (!this.calendarEl) return;
    
    this.setupEventListeners();
    this.renderCalendar();
    this.renderEventList();
  }

  setupEventListeners() {
    const prevBtn = this.querySelector('.jkl-goovi-calendar__nav-prev');
    const nextBtn = this.querySelector('.jkl-goovi-calendar__nav-next');
    
    if (prevBtn) {
      prevBtn.addEventListener('click', () => this.goToPreviousMonth());
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.goToNextMonth());
    }
    
    if (this.searchInput) {
      this.searchInput.addEventListener('input', (e) => this.handleSearch(e.target.value));
    }
    
    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', () => this.handleClearSearch());
    }
  }

  goToPreviousMonth() {
    if (this.currentMonth === 0) {
      this.currentMonth = 11;
      this.currentYear--;
    } else {
      this.currentMonth--;
    }
    this.renderCalendar();
  }

  goToNextMonth() {
    if (this.currentMonth === 11) {
      this.currentMonth = 0;
      this.currentYear++;
    } else {
      this.currentMonth++;
    }
    this.renderCalendar();
  }

  renderCalendar() {
    if (!this.calendarEl) return;
    
    const monthNameEl = this.calendarEl.querySelector('.jkl-goovi-calendar__month-name');
    const yearEl = this.calendarEl.querySelector('.jkl-goovi-calendar__year');
    
    if (monthNameEl) monthNameEl.textContent = this.monthNames[this.currentMonth];
    if (yearEl) yearEl.textContent = this.currentYear;
    
    const daysGrid = this.calendarEl.querySelector('.jkl-goovi-calendar__days-grid');
    if (!daysGrid) return;
    
    const firstDayOfMonth = new Date(this.currentYear, this.currentMonth, 1);
    const lastDayOfMonth = new Date(this.currentYear, this.currentMonth + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();
    
    // Saturday = 0 based week
    let firstDayWeekday = firstDayOfMonth.getDay();
    firstDayWeekday = (firstDayWeekday + 1) % 7;
    
    let html = '';
    
    // Empty cells before month starts
    for (let i = 0; i < firstDayWeekday; i++) {
      html += '<div class="jkl-goovi-calendar__day jkl-goovi-calendar__day--empty"></div>';
    }
    
    // Days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = this.formatDate(day);
      const hasEvent = this.eventDates.includes(dateStr);
      const isSearchResult = this.searchResultDates.includes(dateStr);
      const isSelected = this.selectedDate === dateStr;
      const isClickable = hasEvent || isSearchResult;
      
      let classes = 'jkl-goovi-calendar__day';
      if (hasEvent || isSearchResult) classes += ' jkl-goovi-calendar__day--has-event';
      if (isSelected) classes += ' jkl-goovi-calendar__day--selected';
      
      html += '<div class="' + classes + '"><button type="button" class="jkl-goovi-calendar__day-btn" data-date="' + dateStr + '"' + (!isClickable ? ' disabled' : '') + '>' + day + '</button></div>';
    }
    
    // Fill remaining cells
    const totalCells = firstDayWeekday + daysInMonth;
    const remainingCells = totalCells % 7;
    if (remainingCells !== 0) {
      for (let i = 0; i < 7 - remainingCells; i++) {
        html += '<div class="jkl-goovi-calendar__day jkl-goovi-calendar__day--empty"></div>';
      }
    }
    
    daysGrid.innerHTML = html;
    
    daysGrid.querySelectorAll('.jkl-goovi-calendar__day-btn:not([disabled])').forEach(btn => {
      btn.addEventListener('click', () => this.handleDateClick(btn.dataset.date));
    });
  }

  formatDate(day) {
    const month = (this.currentMonth + 1).toString().padStart(2, '0');
    const dayStr = day.toString().padStart(2, '0');
    return this.currentYear + '-' + month + '-' + dayStr;
  }

  handleDateClick(date) {
    this.selectedDate = date;
    this.selectedEvent = null;
    this.renderCalendar();
    this.renderEventList();
    this.hideEventMap();
  }

  handleSearch(term) {
    this.searchTerm = term;
    
    if (this.clearBtn) {
      this.clearBtn.disabled = !term;
    }
    
    if (term) {
      const lowerTerm = term.toLowerCase();
      this.searchResultDates = [...new Set(
        this.events
          .filter(event => 
            event.eventName.toLowerCase().includes(lowerTerm) ||
            event.address.toLowerCase().includes(lowerTerm)
          )
          .map(event => event.eventDate)
      )];
    } else {
      this.searchResultDates = [];
    }
    
    this.renderCalendar();
    this.renderEventList();
  }

  handleClearSearch() {
    this.searchTerm = '';
    this.searchResultDates = [];
    this.selectedDate = null;
    this.selectedEvent = null;
    
    if (this.searchInput) {
      this.searchInput.value = '';
    }
    if (this.clearBtn) {
      this.clearBtn.disabled = true;
    }
    
    this.renderCalendar();
    this.renderEventList();
    this.hideEventMap();
  }

  renderEventList() {
    if (!this.eventListEl) return;
    
    const headerEl = this.eventListEl.querySelector('.jkl-goovi-event-list__title');
    const itemsEl = this.eventListEl.querySelector('.jkl-goovi-event-list__items');
    const emptyEl = this.eventListEl.querySelector('.jkl-goovi-event-list__empty');
    const emptyTextEl = this.eventListEl.querySelector('.jkl-goovi-event-list__empty-text');
    
    let filteredEvents = [];
    
    if (this.searchTerm) {
      const lowerTerm = this.searchTerm.toLowerCase();
      filteredEvents = this.events.filter(event =>
        event.eventName.toLowerCase().includes(lowerTerm) ||
        event.address.toLowerCase().includes(lowerTerm)
      );
    } else if (this.selectedDate) {
      filteredEvents = this.events.filter(event => event.eventDate === this.selectedDate);
    }
    
    if (headerEl) {
      if (this.searchTerm) {
        headerEl.textContent = (this.t.searchResults || 'Search results ({{ count }})').replace('{{ count }}', filteredEvents.length);
      } else if (this.selectedDate) {
        headerEl.textContent = (this.t.eventsForDate || 'Events for {{ date }}').replace('{{ date }}', this.formatDisplayDate(this.selectedDate));
      } else {
        headerEl.textContent = this.t.selectDate || 'Select a date from the calendar to view events';
      }
    }
    
    if (filteredEvents.length === 0) {
      if (itemsEl) itemsEl.innerHTML = '';
      if (emptyEl) emptyEl.style.display = 'block';
      if (emptyTextEl) {
        if (this.searchTerm) {
          emptyTextEl.textContent = this.t.noResults || 'No events found.';
        } else if (this.selectedDate) {
          emptyTextEl.textContent = this.t.noEventsForDate || 'No events on this date.';
        } else {
          emptyTextEl.textContent = this.t.noEventsAvailable || 'No events available';
        }
      }
    } else {
      if (emptyEl) emptyEl.style.display = 'none';
      if (itemsEl) {
        let eventsHtml = '';
        filteredEvents.forEach((event) => {
          const isSelected = this.selectedEvent && this.selectedEvent.id === event.id;
          eventsHtml += '<button type="button" class="jkl-goovi-event-list__item' + (isSelected ? ' jkl-goovi-event-list__item--selected' : '') + '" data-event-id="' + event.id + '">';
          eventsHtml += '<h4 class="jkl-goovi-event-list__item-name">' + this.escapeHtml(event.eventName) + '</h4>';
          eventsHtml += '<div class="jkl-goovi-event-list__item-details">';
          eventsHtml += '<svg class="jkl-goovi-event-list__item-icon" width="24" height="26" viewBox="0 0 32.898 37.872" fill="currentColor"><path d="M2077.847,8169.743a2.028,2.028,0,0,1-1.3-.471,41.79,41.79,0,0,1-10.824-9.8,20.175,20.175,0,0,1-3.1-5.514,19.766,19.766,0,0,1-1.221-6.165v-.055a15.559,15.559,0,0,1,4.84-11.22,16.63,16.63,0,0,1,11.612-4.642h.054a16.623,16.623,0,0,1,11.614,4.7,15.554,15.554,0,0,1,4.779,11.27v.18c-.069,3.5-1.418,8.96-7.451,15.122a43.088,43.088,0,0,1-7.693,6.128A2.033,2.033,0,0,1,2077.847,8169.743Zm-.346-27.158a4.941,4.941,0,1,0,5.1,4.939A5.029,5.029,0,0,0,2077.5,8142.585Z" transform="translate(-2061.398 -8131.872)"/></svg>';
          eventsHtml += '<div class="jkl-goovi-event-list__item-info">';
          eventsHtml += '<span class="jkl-goovi-event-list__item-address">' + this.escapeHtml(event.address) + '</span>';
          if (event.phoneNumber) {
            eventsHtml += '<span class="jkl-goovi-event-list__item-phone">' + this.escapeHtml(event.phoneNumber) + '</span>';
          }
          eventsHtml += '</div></div></button>';
        });
        itemsEl.innerHTML = eventsHtml;
        
        itemsEl.querySelectorAll('.jkl-goovi-event-list__item').forEach(item => {
          item.addEventListener('click', () => {
            const eventId = item.dataset.eventId;
            const event = this.events.find(e => e.id === eventId);
            if (event) this.handleEventClick(event);
          });
        });
      }
    }
  }

  handleEventClick(event) {
    this.selectedEvent = event;
    this.renderEventList();
    this.showEventMap(event);
  }

  formatDisplayDate(dateStr) {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString(this.locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }

  formatEventDate(dateStr) {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dayNum = date.getDate();
    const monthName = date.toLocaleDateString(this.locale, { month: 'long' }).toUpperCase();
    const yearNum = date.getFullYear();
    return dayNum + ' ' + monthName + ' ' + yearNum;
  }

  showEventMap(event) {
    if (!this.eventMapEl || !this.googleMapsApiKey) return;
    
    this.eventMapEl.style.display = 'block';
    
    const dateEl = this.eventMapEl.querySelector('.jkl-goovi-event-map__date');
    const nameEl = this.eventMapEl.querySelector('.jkl-goovi-event-map__event-name');
    const addressEl = this.eventMapEl.querySelector('.jkl-goovi-event-map__address');
    const phoneEl = this.eventMapEl.querySelector('.jkl-goovi-event-map__phone');
    const shareBtn = this.eventMapEl.querySelector('.jkl-goovi-event-map__share-btn');
    
    if (dateEl) dateEl.textContent = this.formatEventDate(event.eventDate);
    if (nameEl) nameEl.textContent = event.eventName;
    if (addressEl) addressEl.textContent = event.address;
    if (phoneEl) {
      if (event.phoneNumber) {
        phoneEl.textContent = (this.t.phonePrefix || 'Tel.') + ' ' + event.phoneNumber;
        phoneEl.style.display = 'block';
      } else {
        phoneEl.style.display = 'none';
      }
    }
    
    if (shareBtn) {
      shareBtn.onclick = () => {
        const encodedAddress = encodeURIComponent(event.address);
        const mapsUrl = 'https://www.google.com/maps?q=' + encodedAddress;
        window.open(mapsUrl, '_blank');
      };
    }
    
    this.initMap(event);
    this.eventMapEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  hideEventMap() {
    if (this.eventMapEl) {
      this.eventMapEl.style.display = 'none';
    }
  }

  initMap(event) {
    const mapContainer = this.eventMapEl ? this.eventMapEl.querySelector('.jkl-goovi-event-map__map') : null;
    if (!mapContainer || !this.googleMapsApiKey) return;
    
    if (!window.google || !window.google.maps) {
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://maps.googleapis.com/maps/api/js?key=' + this.googleMapsApiKey + '&libraries=places';
        script.async = true;
        script.defer = true;
        script.onload = () => this.createMap(event, mapContainer);
        document.head.appendChild(script);
      } else {
        existingScript.addEventListener('load', () => this.createMap(event, mapContainer));
      }
    } else {
      this.createMap(event, mapContainer);
    }
  }

  createMap(event, container) {
    if (!window.google || !window.google.maps) return;

    const lat = parseFloat(event.latitude);
    const lng = parseFloat(event.longitude);
    const hasLatLng = Number.isFinite(lat) && Number.isFinite(lng);

    const applyLocation = (location) => {
      if (!this.mapInstance) {
        this.mapInstance = new window.google.maps.Map(container, {
          center: location,
          zoom: 15,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: false,
          disableDefaultUI: true,
          styles: [
            { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
            { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] }
          ]
        });
      } else {
        this.mapInstance.setCenter(location);
      }
      if (this.markerInstance) {
        this.markerInstance.setPosition(location);
      } else {
        this.markerInstance = new window.google.maps.Marker({
          position: location,
          map: this.mapInstance,
          icon: {
            url: 'data:image/svg+xml;base64,' + btoa('<svg width="48" height="48" xmlns="http://www.w3.org/2000/svg"><g transform="translate(8, 4)"><path d="M16 0C10.5 0 6 4.5 6 10c0 7.4 10 18 10 18s10-10.6 10-18c0-5.5-4.5-10-10-10zm0 13c-1.7 0-3-1.3-3-3s1.3-3 3-3 3 1.3 3 3-1.3 3-3 3z" fill="#b37fe6"/></g></svg>'),
            scaledSize: new window.google.maps.Size(48, 48),
            anchor: new window.google.maps.Point(24, 48)
          },
          title: event.eventName
        });
      }
    };

    if (hasLatLng) {
      const location = { lat, lng };
      applyLocation(location);
      return;
    }

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: event.address }, (results, status) => {
      if (status === 'OK' && results[0]) {
        applyLocation(results[0].geometry.location);
      }
    });
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  disconnectedCallback() {}
}

if (!customElements.get('jkl-goovi-day-events')) {
  customElements.define('jkl-goovi-day-events', JklGooviDayEvents);
}
